import { TypographyV3, type TypographyV3Props } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import React, { useEffect, useState } from 'react';

export interface CareersTextAnimationProps
  extends ComponentPropsWithoutRef<'div'> {
  /** set the delay before the animation fires, in ms */
  readonly delay?: number;
  /** set the duration of the animation, in ms */
  readonly duration?: number;
  /** optional element for text */
  readonly Element?: TypographyV3Props['Element'];
  /** text to animate over the start text */
  readonly endText: string;
  /** initial text to display */
  readonly startText: string;
  /** optional variant for text */
  readonly variant?: TypographyV3Props['variant'];
  /** optional weight for text */
  readonly weight?: TypographyV3Props['weight'];
}

const CareersTextAnimation = ({
  className = '',
  delay = 2000,
  duration = 1000,
  Element = 'p',
  endText,
  startText,
  variant = 'h1',
  weight = 'light',
  ...props
}: CareersTextAnimationProps): JSX.Element => {
  const [animate, setAnimate] = useState(false);

  const widthAnim = !animate ? { width: '0px' } : {};
  const convertDuration = (dur: number) => `${dur / 1000}s`;

  useEffect(() => {
    setTimeout(() => {
      setAnimate(true);
    }, delay);
  });

  return (
    <div className={cx('relative', className)} {...props}>
      <TypographyV3
        variant={variant}
        Element={Element}
        weight={weight}
        height="tight"
      >
        {startText}
      </TypographyV3>
      <TypographyV3
        className={cx(
          'absolute inset-0 overflow-hidden border-r-4 border-solid border-blue-200 bg-sandstone-400',
          'w-[94px] whitespace-nowrap transition-[width] md:w-[140px] lg:w-[190px]',
        )}
        style={{
          transitionDuration: convertDuration(duration),
          ...widthAnim,
        }}
        variant={variant}
        Element={Element}
        weight={weight}
        height="tight"
      >
        {endText}
      </TypographyV3>
    </div>
  );
};

export default CareersTextAnimation;
